/* eslint-disable react/no-unknown-property */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import {
  ArrowRight, Play, Atom, Dna, Microscope, Zap, Activity,
  Menu, X, ChevronRight, Sparkles, Github, Twitter, Heart,
  Globe, ShieldCheck, FlaskConical, GraduationCap, Users, Moon, Sun,
  CheckCircle
} from "lucide-react";
import { useThemeStore } from "./components/guided/titration/store/themeStore";

import azriImg from "./logo/team/azri.png";
import aimanImg from "./logo/team/aiman.jpeg";
import naimImg from "./logo/team/naim.jpeg";

// ─── THEME-AWARE 3D BACKGROUND ─────────────────────────────────────────────

interface ThemeProps { isDark: boolean }

const FloatingShapes: React.FC<ThemeProps> = ({ isDark }) => {
  const r1 = useRef<THREE.Mesh>(null);
  const r2 = useRef<THREE.Mesh>(null);
  const r3 = useRef<THREE.Mesh>(null);
  const r4 = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (r1.current) {
      r1.current.rotation.x = t * 0.2;
      r1.current.rotation.y = t * 0.3;
      r1.current.position.y = Math.sin(t * 0.5) * 0.2;
    }
    if (r2.current) {
      r2.current.rotation.x = t * 0.15;
      r2.current.rotation.z = t * 0.25;
      r2.current.position.y = Math.sin(t * 0.4 + 1) * 0.15;
    }
    if (r3.current) {
      r3.current.rotation.y = t * 0.2;
      r3.current.rotation.z = t * 0.1;
      r3.current.position.y = Math.sin(t * 0.6 + 2) * 0.1;
    }
    if (r4.current) {
      r4.current.rotation.x = t * 0.12;
      r4.current.rotation.y = t * 0.18;
      r4.current.position.y = Math.sin(t * 0.35 + 3) * 0.18;
    }
  });

  const c1 = isDark ? "#8b5cf6" : "#2563eb";  // violet / blue
  const c2 = isDark ? "#06b6d4" : "#0d9488";  // cyan / teal
  const c3 = isDark ? "#d946ef" : "#7c3aed";  // magenta / purple
  const c4 = isDark ? "#a78bfa" : "#3b82f6";  // light-violet / sky-blue
  const op = isDark ? 0.35 : 0.22;

  return (
    <>
      <mesh ref={r1} position={[-3.5, 2, -2]}>
        <icosahedronGeometry args={[0.65, 0]} />
        <meshBasicMaterial color={c1} wireframe transparent opacity={op} />
      </mesh>
      <mesh ref={r2} position={[3.8, -1.8, -3]}>
        <octahedronGeometry args={[0.55, 0]} />
        <meshBasicMaterial color={c2} wireframe transparent opacity={op * 0.85} />
      </mesh>
      <mesh ref={r3} position={[2.8, 2.8, -4]}>
        <torusGeometry args={[0.45, 0.16, 8, 20]} />
        <meshBasicMaterial color={c3} wireframe transparent opacity={op * 0.6} />
      </mesh>
      <mesh ref={r4} position={[-4, -2.5, -3.5]}>
        <dodecahedronGeometry args={[0.5, 0]} />
        <meshBasicMaterial color={c4} wireframe transparent opacity={op * 0.5} />
      </mesh>
    </>
  );
};

const AmbientParticles: React.FC<ThemeProps> = ({ isDark }) => {
  const ref1 = useRef<THREE.Points>(null);
  const ref2 = useRef<THREE.Points>(null);
  const count = 90;

  const { p1, v1, p2, v2 } = useMemo(() => {
    const p1 = new Float32Array(count * 3);
    const v1 = new Float32Array(count * 3);
    const p2 = new Float32Array(count * 3);
    const v2 = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      p1[i*3]   = (Math.random()-0.5)*12;
      p1[i*3+1] = (Math.random()-0.5)*8;
      p1[i*3+2] = (Math.random()-0.5)*6-3;
      v1[i*3]   = (Math.random()-0.5)*0.0018;
      v1[i*3+1] = (Math.random()-0.5)*0.0018;
      p2[i*3]   = (Math.random()-0.5)*12;
      p2[i*3+1] = (Math.random()-0.5)*8;
      p2[i*3+2] = (Math.random()-0.5)*6-3;
      v2[i*3]   = (Math.random()-0.5)*0.0022;
      v2[i*3+1] = (Math.random()-0.5)*0.0022;
    }
    return { p1, v1, p2, v2 };
  }, []);

  useFrame(() => {
    const drift = (arr: Float32Array, vel: Float32Array) => {
      for (let i = 0; i < count; i++) {
        arr[i*3]   += vel[i*3];
        arr[i*3+1] += vel[i*3+1];
        if (arr[i*3]   >  6) arr[i*3]   = -6;
        if (arr[i*3]   < -6) arr[i*3]   =  6;
        if (arr[i*3+1] >  4) arr[i*3+1] = -4;
        if (arr[i*3+1] < -4) arr[i*3+1] =  4;
      }
    };
    if (ref1.current) {
      drift(ref1.current.geometry.attributes.position.array as Float32Array, v1);
      ref1.current.geometry.attributes.position.needsUpdate = true;
    }
    if (ref2.current) {
      drift(ref2.current.geometry.attributes.position.array as Float32Array, v2);
      ref2.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  const col1 = isDark ? "#8b5cf6" : "#2563eb";
  const col2 = isDark ? "#06b6d4" : "#0d9488";
  const op1  = isDark ? 0.70 : 0.45;
  const op2  = isDark ? 0.55 : 0.38;

  return (
    <>
      <points ref={ref1}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={count} array={p1} itemSize={3} args={[p1, 3]} />
        </bufferGeometry>
        <pointsMaterial size={0.035} color={col1} transparent opacity={op1} sizeAttenuation />
      </points>
      <points ref={ref2}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={count} array={p2} itemSize={3} args={[p2, 3]} />
        </bufferGeometry>
        <pointsMaterial size={0.028} color={col2} transparent opacity={op2} sizeAttenuation />
      </points>
    </>
  );
};

// ─── INLINE THEME TOGGLE ───────────────────────────────────────────────────

const ThemeToggle: React.FC<{ className?: string }> = ({ className = "" }) => {
  const { resolvedTheme, setTheme } = useThemeStore();
  const isDark = resolvedTheme === "dark";
  return (
    <button
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className={`p-2 rounded-full border transition-all duration-300 flex items-center justify-center ${
        isDark
          ? "bg-white/5 border-white/15 text-gray-300 hover:text-white hover:bg-white/10"
          : "bg-white/80 border-slate-200 text-slate-600 hover:text-blue-600 hover:bg-white shadow-sm"
      } ${className}`}
      title={`Switch to ${isDark ? "light" : "dark"} mode`}
    >
      {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
};

// ─── HEADER ────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { to: "#", label: "Home" },
  { to: "#features", label: "Features" },
  { to: "#about", label: "About Us" },
  { to: "#pricing", label: "Pricing" },
];

const Header: React.FC = () => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "dark";
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobileOpen]);

  const scrolledBg = isDark
    ? "backdrop-blur-xl bg-[#030712]/80 border-b border-white/8"
    : "backdrop-blur-xl bg-white/85 border-b border-slate-200/80 shadow-sm";

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? `py-2 ${scrolledBg}` : "py-4 bg-transparent border-b border-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 relative z-50">
              <img src="/logo_only.png" alt="HoloLab" className="w-10 h-10 object-contain" />
              <span className={`text-xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                Holo<span className={isDark ? "text-[#61DAFB]" : "text-blue-600"}>Lab</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className={`hidden md:flex items-center gap-1 p-1 rounded-full border backdrop-blur-md ${isDark ? "bg-white/5 border-white/8" : "bg-white/70 border-slate-200"}`}>
              {NAV_LINKS.map((link) => (
                link.to.startsWith('/') && link.to !== '/' ? (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      isDark ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                    }`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.to}
                    href={link.to}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      isDark ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                    }`}
                  >
                    {link.label}
                  </a>
                )
              ))}
            </nav>

            {/* Actions */}
            <div className="hidden md:flex items-center gap-2">
              <ThemeToggle />
              <Link to="/sign-in" className={`text-sm font-medium px-4 py-2 rounded-lg transition-all ${isDark ? "text-gray-400 hover:text-white hover:bg-white/5" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                Sign In
              </Link>
              <Link to="/sign-up" className={`text-sm font-medium px-4 py-2 rounded-lg border transition-all ${isDark ? "text-white border-white/15 hover:bg-white/5" : "text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                Sign Up
              </Link>
              <Link
                to="/sign-in"
                className={`relative group overflow-hidden px-5 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 transition-all duration-300 ${
                  isDark
                    ? "bg-[#61DAFB] text-[#0A1929] shadow-[0_0_20px_rgba(97,218,251,0.3)] hover:shadow-[0_0_30px_rgba(97,218,251,0.5)]"
                    : "bg-blue-600 text-white shadow-md shadow-blue-500/25 hover:bg-blue-700"
                }`}
              >
                <span className="relative z-10 flex items-center gap-2">Launch Lab <ChevronRight size={16} /></span>
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              </Link>
            </div>

            {/* Mobile toggle */}
            <div className="md:hidden flex items-center gap-2">
              <ThemeToggle />
              <button onClick={() => setIsMobileOpen(!isMobileOpen)} className={`relative z-50 p-2 transition-colors ${isDark ? "text-gray-300 hover:text-white" : "text-slate-600 hover:text-slate-900"}`}>
                {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileOpen && (
        <div className={`fixed inset-0 z-40 md:hidden pt-24 px-6 flex flex-col backdrop-blur-xl ${isDark ? "bg-[#030712]/95" : "bg-white/95"}`}>
          <div className="flex flex-col gap-6">
            {NAV_LINKS.map((link) => (
              link.to.startsWith('/') && link.to !== '/' ? (
                <Link key={link.to} to={link.to} onClick={() => setIsMobileOpen(false)}
                  className={`block text-2xl font-bold pb-4 border-b transition-colors ${isDark ? "text-white/80 hover:text-[#61DAFB] border-white/10" : "text-slate-700 hover:text-blue-600 border-slate-200"}`}>
                  {link.label}
                </Link>
              ) : (
                <a key={link.to} href={link.to} onClick={() => setIsMobileOpen(false)}
                  className={`block text-2xl font-bold pb-4 border-b transition-colors ${isDark ? "text-white/80 hover:text-[#61DAFB] border-white/10" : "text-slate-700 hover:text-blue-600 border-slate-200"}`}>
                  {link.label}
                </a>
              )
            ))}
            <div className="mt-4 flex flex-col gap-3">
              <Link to="/sign-in" onClick={() => setIsMobileOpen(false)}
                className={`w-full py-4 rounded-xl border font-bold text-lg flex items-center justify-center transition-all ${isDark ? "border-white/15 text-white hover:bg-white/5" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                Sign In
              </Link>
              <Link to="/sign-up" onClick={() => setIsMobileOpen(false)}
                className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 ${isDark ? "bg-[#61DAFB] text-[#0A1929]" : "bg-blue-600 text-white"}`}>
                <Sparkles size={20} /> Get Started Free
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── HERO SECTION ──────────────────────────────────────────────────────────

const HolographicPortal: React.FC<ThemeProps> = ({ isDark }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current || !innerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const rX = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
    const rY = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
    innerRef.current.style.transform = `rotateX(${rX}deg) rotateY(${rY}deg)`;
  };

  const handleMouseLeave = () => {
    if (innerRef.current) innerRef.current.style.transform = "rotateX(0deg) rotateY(0deg)";
  };

  return (
    <div ref={containerRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}
      className="relative w-full aspect-video" style={{ perspective: "1000px" }}>
      <div ref={innerRef} className="w-full h-full relative"
        style={{ transformStyle: "preserve-3d", transition: "transform 0.1s ease-out" }}>

        {/* Glow behind */}
        <div style={{ transform: "translateZ(-40px)" }}
          className={`absolute inset-4 rounded-lg blur-[80px] transition-all duration-500 ${isDark ? "bg-gradient-to-tr from-[#61DAFB]/20 to-purple-500/20" : "bg-gradient-to-tr from-blue-400/15 to-teal-400/15"}`} />

        {/* Screen */}
        <div className={`absolute inset-0 rounded-xl overflow-hidden shadow-2xl border ${isDark ? "bg-[#020617] border-white/10" : "bg-[#f8faff] border-slate-200"}`}>
          <video 
            className="w-full h-full object-cover opacity-90 cursor-pointer relative z-10" 
            autoPlay 
            loop 
            muted 
            playsInline
            onClick={(e) => {
              const video = e.currentTarget;
              if (video.requestFullscreen) {
                video.requestFullscreen();
                video.muted = false;
                video.controls = true;
              } else if ((video as any).webkitRequestFullscreen) {
                (video as any).webkitRequestFullscreen();
                video.muted = false;
                video.controls = true;
              }
            }}
          >
            <source src="/Video/Demo.mp4" type="video/mp4" />
          </video>

          {/* Fallback */}
          <div className={`absolute inset-0 flex items-center justify-center ${isDark ? "bg-gradient-to-br from-[#0d1b2a] via-[#020617] to-[#0d0d1a]" : "bg-gradient-to-br from-[#f0f4ff] via-[#e8eeff] to-[#f0f9ff]"}`}>
            <div className="text-center">
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className={`absolute inset-0 rounded-full border-2 animate-spin ${isDark ? "border-[#61DAFB]/20 border-t-[#61DAFB]" : "border-blue-200 border-t-blue-600"}`} />
                <div className={`absolute inset-3 rounded-full border animate-spin ${isDark ? "border-purple-500/20 border-b-purple-500" : "border-teal-200 border-b-teal-600"}`}
                  style={{ animationDuration: "2s", animationDirection: "reverse" }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <FlaskConical className={`w-8 h-8 ${isDark ? "text-[#61DAFB]" : "text-blue-600"}`} />
                </div>
              </div>
              <p className={`text-sm font-mono tracking-[0.2em] ${isDark ? "text-[#61DAFB]" : "text-blue-600"}`}>SIMULATION READY</p>
              <p className={`text-xs mt-1 ${isDark ? "text-white/30" : "text-slate-400"}`}>HoloLab v2.4 · Chemistry Engine</p>
            </div>
          </div>

          {/* HUD */}
          <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-start bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[10px] font-mono text-white/70 tracking-widest">LIVE FEED</span>
            </div>
            <div className="px-2 py-1 bg-white/10 backdrop-blur rounded text-[10px] font-mono text-white">60 FPS</div>
          </div>

          {/* Scanner line */}
          <div className={`absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent to-transparent pointer-events-none ${isDark ? "via-[#61DAFB]/40" : "via-blue-500/30"}`}
            style={{ animation: "scan 3s linear infinite", top: 0 }} />
        </div>

        {/* Floating badges */}
        <div style={{ transform: "translateZ(30px)" }}
          className={`absolute -right-4 top-1/3 backdrop-blur p-3 rounded-lg shadow-xl border ${isDark ? "bg-[#020617]/90 border-purple-500/30" : "bg-white/90 border-purple-200"}`}>
          <Zap className={`w-4 h-4 mb-1 ${isDark ? "text-purple-400" : "text-purple-600"}`} />
          <div className={`text-[10px] font-mono ${isDark ? "text-gray-400" : "text-slate-500"}`}>450 kJ</div>
          <div className={`text-[9px] font-mono ${isDark ? "text-purple-400" : "text-purple-600"}`}>Energy</div>
        </div>
        <div style={{ transform: "translateZ(20px)" }}
          className={`absolute -left-4 bottom-1/4 backdrop-blur p-3 rounded-lg shadow-xl border ${isDark ? "bg-[#020617]/90 border-[#61DAFB]/20" : "bg-white/90 border-blue-200"}`}>
          <Activity className={`w-4 h-4 mb-1 ${isDark ? "text-[#61DAFB]" : "text-blue-600"}`} />
          <div className={`text-[10px] font-mono ${isDark ? "text-gray-400" : "text-slate-500"}`}>pH 7.0</div>
          <div className={`text-[9px] font-mono ${isDark ? "text-[#61DAFB]" : "text-blue-600"}`}>Neutral</div>
        </div>
      </div>
    </div>
  );
};

const HeroSection: React.FC = () => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "dark";

  return (
    <section className={`relative min-h-screen flex items-center justify-center overflow-hidden pt-20 lg:pt-0 transition-colors duration-500 ${isDark ? "bg-[#030712]" : "bg-[#f0f4ff]"}`}>

      {/* Aurora blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-1/4 -left-1/4 w-[70vw] h-[70vw] rounded-full animate-aurora"
          style={{ background: isDark
            ? "radial-gradient(circle, rgba(139,92,246,0.14) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(37,99,235,0.09) 0%, transparent 65%)" }} />
        <div className="absolute -bottom-1/4 -right-1/4 w-[60vw] h-[60vw] rounded-full animate-aurora2"
          style={{ background: isDark
            ? "radial-gradient(circle, rgba(6,182,212,0.10) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(13,148,136,0.08) 0%, transparent 65%)" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40vw] h-[40vw] rounded-full animate-aurora"
          style={{ animationDelay: "4s", background: isDark
            ? "radial-gradient(circle, rgba(217,70,239,0.06) 0%, transparent 60%)"
            : "radial-gradient(circle, rgba(124,58,237,0.05) 0%, transparent 60%)" }} />
      </div>

      {/* Matrix grid */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: isDark
            ? "linear-gradient(rgba(139,92,246,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.06) 1px, transparent 1px)"
            : "linear-gradient(rgba(37,99,235,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.05) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
          maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, #000 60%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, #000 60%, transparent 100%)",
        }} />

      {/* 3D Canvas background */}
      <div className="absolute inset-0">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }} gl={{ alpha: true }}>
          <FloatingShapes isDark={isDark} />
          <AmbientParticles isDark={isDark} />
        </Canvas>
      </div>

      {/* Content */}
      <div className="max-w-7xl w-full mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center relative z-10 py-20 lg:py-0">

        {/* Left column */}
        <div className="space-y-8 text-center lg:text-left">

          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-mono tracking-widest mx-auto lg:mx-0 ${isDark ? "border-[#61DAFB]/20 bg-[#61DAFB]/5 text-[#61DAFB]" : "border-blue-200 bg-blue-50 text-blue-600"}`}>
            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${isDark ? "bg-[#61DAFB]" : "bg-blue-600"}`} />
            AR-POWERED CHEMISTRY LAB
          </div>

          <h1 className={`text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] ${isDark ? "text-white" : "text-slate-900"}`}>
            The Universe is <br />
            Your{" "}
            <span className="text-transparent bg-clip-text"
              style={{
                backgroundImage: isDark
                  ? "linear-gradient(to right, #61DAFB, #ffffff, #a78bfa)"
                  : "linear-gradient(to right, #2563eb, #0d9488, #7c3aed)",
                backgroundSize: "200%",
                animation: "shimmer 4s linear infinite",
              }}>
              Laboratory.
            </span>
          </h1>

          <div className={`text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            <p className="mb-4">Master abstract concepts through gesture control. The world's first browser-based AR learning engine for:</p>
            <div className="flex flex-wrap justify-center lg:justify-start gap-3">
              <span className={`flex items-center gap-1.5 px-3 py-1 rounded border text-sm ${isDark ? "border-[#61DAFB]/20 bg-[#61DAFB]/5 text-[#61DAFB]" : "border-blue-200 bg-blue-50 text-blue-700"}`}>
                <Atom size={13} /> Chemistry
              </span>
              <span className={`flex items-center gap-1.5 px-3 py-1 rounded border text-sm ${isDark ? "border-green-500/20 bg-green-500/5 text-green-400" : "border-teal-200 bg-teal-50 text-teal-700"}`}>
                <Dna size={13} /> Biology
              </span>
              <span className={`flex items-center gap-1.5 px-3 py-1 rounded border text-sm ${isDark ? "border-purple-500/20 bg-purple-500/5 text-purple-400" : "border-purple-200 bg-purple-50 text-purple-700"}`}>
                <Microscope size={13} /> Physics
              </span>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Link to="/sign-up"
              className={`group relative px-8 py-4 font-bold rounded-lg overflow-hidden transition-all hover:scale-[1.02] flex items-center justify-center gap-2 ${
                isDark
                  ? "bg-[#61DAFB] text-[#020617] hover:shadow-[0_0_40px_rgba(97,218,251,0.4)]"
                  : "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/25 hover:-translate-y-0.5"
              }`}>
              <div className="absolute inset-0 bg-white/40 -translate-x-full group-hover:translate-x-full transition-transform duration-500 ease-out skew-x-12" />
              <span className="relative flex items-center gap-2">Get Started Free <ArrowRight size={18} /></span>
            </Link>
            <Link to="/sign-in"
              className={`px-8 py-4 rounded-lg border font-medium transition-all flex items-center justify-center gap-3 group ${isDark ? "border-white/10 text-white hover:bg-white/5" : "border-slate-300 text-slate-700 hover:bg-white hover:shadow-md"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors group-hover:text-black ${isDark ? "bg-white/10 group-hover:bg-white" : "bg-slate-100 group-hover:bg-blue-600 group-hover:text-white"}`}>
                <Play size={12} fill="currentColor" />
              </div>
              Sign In
            </Link>
          </div>

          {/* Social proof */}
          <div className={`flex items-center gap-4 justify-center lg:justify-start text-sm ${isDark ? "text-slate-500" : "text-slate-400"}`}>
            <div className="flex items-center gap-1.5">
              <GraduationCap size={15} className={isDark ? "text-[#61DAFB]" : "text-blue-500"} />
              <span>For Educators</span>
            </div>
            <span className={`w-px h-4 ${isDark ? "bg-white/10" : "bg-slate-300"}`} />
            <div className="flex items-center gap-1.5">
              <Users size={15} className={isDark ? "text-purple-400" : "text-purple-500"} />
              <span>For Students</span>
            </div>
            <span className={`w-px h-4 ${isDark ? "bg-white/10" : "bg-slate-300"}`} />
            <div className="flex items-center gap-1.5">
              <Zap size={15} className="text-green-500" />
              <span>Free to Try</span>
            </div>
          </div>
        </div>

        {/* Right column */}
        <HolographicPortal isDark={isDark} />
      </div>

      <style>{`@keyframes scan { 0% { top: 0; } 100% { top: 100%; } }`}</style>
    </section>
  );
};

// ─── FEATURES SECTION ──────────────────────────────────────────────────────

const FEATURES = [
  { icon: Microscope, label: "Real Lab Simulations", desc: "Acid-base titrations, metal reactions — physics-accurate and interactive.", darkColor: "border-[#61DAFB]/20 bg-[#61DAFB]/5", lightColor: "border-blue-100 bg-blue-50", iconDark: "text-[#61DAFB]", iconLight: "text-blue-600" },
  { icon: Atom, label: "AR Gesture Control", desc: "Use your hands to operate virtual lab equipment — no mouse, no keyboard.", darkColor: "border-purple-500/20 bg-purple-500/5", lightColor: "border-purple-100 bg-purple-50", iconDark: "text-purple-400", iconLight: "text-purple-600" },
  { icon: GraduationCap, label: "Teacher Dashboard", desc: "Create experiments, assign pre-lab assessments, and monitor student progress.", darkColor: "border-green-500/20 bg-green-500/5", lightColor: "border-green-100 bg-green-50", iconDark: "text-green-400", iconLight: "text-green-600" },
  { icon: Zap, label: "Instant Feedback", desc: "AI-powered guidance and scoring keeps students on track without intervention.", darkColor: "border-yellow-500/20 bg-yellow-500/5", lightColor: "border-amber-100 bg-amber-50", iconDark: "text-yellow-400", iconLight: "text-amber-600" },
];

const FeaturesSection: React.FC = () => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "dark";

  return (
    <section id="features" className={`py-24 px-6 transition-colors duration-500 ${isDark ? "bg-[#030712]" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-mono tracking-widest mb-4 ${isDark ? "border-white/10 bg-white/5 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
            PLATFORM CAPABILITIES
          </div>
          <h2 className={`text-4xl md:text-5xl font-bold mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>
            Everything a lab needs.<br />
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: isDark ? "linear-gradient(to right, #61DAFB, #a78bfa)" : "linear-gradient(to right, #2563eb, #0d9488)" }}>
              None of the hazards.
            </span>
          </h2>
          <p className={`max-w-lg mx-auto ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            HoloLab brings the full chemistry lab experience into the browser — safely, instantly, for everyone.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f) => (
            <div key={f.label}
              className={`p-6 rounded-2xl border backdrop-blur-sm hover:scale-[1.02] transition-all duration-300 group ${isDark ? f.darkColor : f.lightColor}`}>
              <div className={`mb-4 p-2 w-fit rounded-lg group-hover:scale-110 transition-transform ${isDark ? "bg-white/5" : "bg-white"}`}>
                <f.icon className={`w-6 h-6 ${isDark ? f.iconDark : f.iconLight}`} />
              </div>
              <h3 className={`font-bold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>{f.label}</h3>
              <p className={`text-sm leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── ABOUT US SECTION ────────────────────────────────────────────────────────

const AboutUsSection: React.FC = () => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "dark";

  const textMuted = isDark ? "text-slate-400" : "text-slate-500";
  const headingGrad = isDark
    ? "bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent"
    : "bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent";

  const teamMembers = [
    {
      name: "Mohammad Azri bin Aziz",
      role: "CEO",
      description: "Leading the core technical development and driving the overall architectural vision for HoloLab.",
      program: "Bachelor of Information Technology (Hons) in Computer System Security",
      abbr: "BIT (BCSS)",
      image: azriImg,
      icon: <Github className="w-5 h-5 text-indigo-400" />
    },
    {
      name: "Nur Aiman Zaharin bin Rahimy",
      role: "CTO",
      description: "Managing server infrastructures, deployments, and ensuring high availability for our systems.",
      program: "Bachelor of Software Engineering (Hons)",
      abbr: "BSE",
      image: aimanImg,
      icon: <Globe className="w-5 h-5 text-teal-400" />
    },
    {
      name: "Muhammad Naim bin Mohammad Fodzi",
      role: "CFO",
      description: "Handling product strategies, external communications, and overall project lifecycle management.",
      program: "Bachelor of Software Engineering (Hons)",
      abbr: "BSE",
      image: naimImg,
      icon: <Globe className="w-5 h-5 text-teal-400" />
    }
  ];

  return (
    <section id="about" className={`relative py-32 px-6 transition-colors duration-500 overflow-hidden ${isDark ? "bg-[#050b14]" : "bg-white"}`}>
      
      {/* Decorative Blur Backgrounds */}
      {isDark ? (
        <>
          <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-violet-600/10 blur-[120px] rounded-full pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-cyan-600/10 blur-[100px] rounded-full pointer-events-none" />
        </>
      ) : (
        <>
          <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-blue-400/5 blur-[120px] rounded-full pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-teal-400/5 blur-[100px] rounded-full pointer-events-none" />
        </>
      )}

      <div className="max-w-5xl mx-auto relative z-10">
        
        {/* Header */}
        <div className="text-center mb-24 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[150px] blur-[60px] pointer-events-none"
             style={{ background: isDark ? "rgba(139,92,246,0.05)" : "rgba(37,99,235,0.03)" }} />

          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-8 border ${isDark ? "bg-violet-500/10 text-violet-400 border-violet-500/20" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span>
            </span>
            UniKL MIIT TEAM
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-black tracking-tighter mb-6 relative">
            <span className={`absolute -inset-1 blur-2xl opacity-20 ${isDark ? "bg-cyan-500" : "bg-blue-500"}`}></span>
            <span className={`relative ${isDark ? "text-white" : "text-slate-900"}`}>Meet the <span className={headingGrad}>Builders</span></span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg sm:text-xl font-medium leading-relaxed ${textMuted}`}>
            We are final year software engineering students driven by the vision to revolutionize chemistry education through interactive, accessible, and cutting-edge web technologies.
          </p>
        </div>

        {/* ADVANCED BENTO STYLE GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {teamMembers.map((member, idx) => {
            const isFeatured = idx === 0;
            return (
              <div 
                key={idx} 
                className={`group relative p-8 sm:p-10 rounded-[2.5rem] border backdrop-blur-xl transition-all duration-700 ease-out hover:-translate-y-2
                  ${isDark ? "bg-white/[0.02] border-white/10 hover:bg-white/[0.05] hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.25)] hover:border-violet-500/40" : "bg-white border-slate-200 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] hover:border-blue-300"} 
                  ${isFeatured ? "md:col-span-2 flex flex-col md:flex-row items-center md:items-stretch text-center md:text-left gap-8" : "flex flex-col items-center text-center"}`}
              >
                {/* Techy background corner glow on hover */}
                {isDark && <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-violet-500/0 via-transparent to-cyan-500/0 group-hover:from-violet-500/10 group-hover:to-cyan-500/10 transition-all duration-700 pointer-events-none opacity-0 group-hover:opacity-100" />}
                
                {/* Avatar / Image */}
                <div className={`relative flex-shrink-0 rounded-[2rem] mb-6 md:mb-0 flex items-center justify-center font-bold shadow-inner overflow-hidden 
                  transition-all duration-700 group-hover:scale-105 group-hover:-rotate-2 group-hover:shadow-[0_0_25px_rgba(139,92,246,0.3)]
                  ${isFeatured ? "w-40 h-40 text-5xl" : "w-36 h-36 text-4xl"} 
                  ${isDark ? "bg-gradient-to-br from-violet-900/50 to-cyan-900/50 border border-white/10" : "bg-gradient-to-br from-blue-100 to-teal-50 border border-slate-200"}`}>
                  
                  <span className={headingGrad}>{member.name.split(' ').map(n=>n[0]).slice(0,2).join('')}</span>
                  
                  {member.image && (
                    <img 
                      src={member.image} 
                      alt={member.name} 
                      className="absolute inset-0 w-full h-full object-cover z-10 transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  {/* Glowing ring overlay */}
                  <div className={`absolute inset-0 rounded-[2rem] border-2 z-20 pointer-events-none ${isDark ? "border-white/5 group-hover:border-cyan-400/50" : "border-black/5 group-hover:border-blue-400/50"} transition-colors duration-700`}></div>
                </div>
                
                <div className={`flex flex-col ${isFeatured ? "md:items-start md:text-left flex-1 md:py-2" : "items-center"}`}>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4 border transition-colors duration-500
                    ${isDark ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/20 group-hover:bg-cyan-500/20" : "bg-teal-500/10 text-teal-700 border-teal-500/20 group-hover:bg-teal-500/20"}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
                    {member.role}
                  </div>
                  
                  <h3 className={`text-2xl sm:text-3xl font-extrabold mb-4 tracking-tight transition-all duration-300 transform-gpu
                    ${isDark ? "text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-violet-400 group-hover:to-cyan-400" : "text-slate-900 group-hover:text-blue-600"}`}>
                    {member.name}
                  </h3>

                  <p className={`text-base sm:text-lg mb-8 leading-relaxed flex-1 ${textMuted}`}>{member.description}</p>
                  
                  <div className={`w-full pt-6 border-t flex flex-col gap-2 transition-colors duration-500 relative
                    ${isDark ? "border-white/10 group-hover:border-white/20" : "border-slate-100 group-hover:border-slate-200"}
                    ${isFeatured ? "md:flex-row md:items-center md:justify-between" : "items-center text-center"}`}>
                    <div className={`flex flex-col ${isFeatured ? "md:items-start" : "items-center"}`}>
                      <span className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${textMuted}`}>Program</span>
                      <span className={`text-sm font-bold ${isDark ? "text-slate-300" : "text-slate-700"}`}>{member.program}</span>
                    </div>
                    <div className={`inline-flex items-center px-4 py-2 text-xs font-bold rounded-xl whitespace-nowrap transition-transform duration-500 group-hover:scale-105
                      ${isDark ? "bg-white/5 text-slate-300 group-hover:bg-white/10" : "bg-slate-50 text-slate-600 group-hover:bg-slate-100 border border-slate-200"}
                      ${isFeatured ? "mt-4 md:mt-0" : "mt-4"}
                    `}>
                      {member.icon && <span className="mr-2 opacity-80">{member.icon}</span>}
                      {member.abbr}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

// ─── PRICING SECTION ─────────────────────────────────────────────────────────

const PricingSection: React.FC = () => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "dark";

  return (
    <section id="pricing" className={`relative py-32 px-6 transition-colors duration-500 overflow-hidden ${isDark ? "bg-[#030712]" : "bg-[#f8fafc]"}`}>
      <div className="absolute inset-0 top-0 w-full h-[500px] pointer-events-none"
        style={{
          background: isDark
            ? "radial-gradient(ellipse at top, rgba(99,102,241,0.08), transparent 70%)"
            : "radial-gradient(ellipse at top, rgba(79,70,229,0.06), transparent 70%)"
        }} />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-24 relative">
          <div className={`inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-8 border backdrop-blur-sm ${isDark ? "bg-indigo-500/10 text-indigo-400 border-indigo-500/20" : "bg-indigo-50 text-indigo-700 border-indigo-200"}`}>
            Accessible Education
          </div>
          <h2 className={`text-4xl md:text-5xl lg:text-7xl font-black tracking-tighter mb-6 ${isDark ? "text-white" : "text-slate-900"}`}>
            Simple, transparent <span className={isDark ? "text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400" : "text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600"}>pricing</span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg md:text-xl font-medium ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            Whether you're an individual explorer or a massive district, we have a plan designed specifically for your needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          {/* Free Tier */}
          <div className={`relative p-8 md:p-10 rounded-[2.5rem] border backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 flex flex-col justify-between
            ${isDark ? "bg-white/[0.02] border-white/5 hover:border-slate-600/50 hover:bg-white/[0.04]" : "bg-white border-slate-200 shadow-lg shadow-slate-200/40 hover:shadow-xl hover:border-slate-300"}`}>
            <div>
              <div className="inline-block px-3 py-1 bg-slate-100 dark:bg-white/10 rounded-full text-xs font-bold text-slate-600 dark:text-slate-300 mb-4 tracking-widest uppercase">Student Explorer</div>
              <h3 className={`text-3xl font-black mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>Free Tier</h3>
              <p className={`text-sm mb-6 h-12 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>Designed for individual students to experience the magic of gesture-based chemistry.</p>
              <div className={`text-5xl font-black mb-1 ${isDark ? "text-white" : "text-slate-900"}`}>RM 0</div>
              <div className={`text-sm font-semibold mb-8 uppercase tracking-widest ${isDark ? "text-slate-500" : "text-slate-400"}`}>Individual Students</div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-emerald-400" : "text-emerald-500"}`} />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}><strong>Sandbox:</strong> 1 hour/day element exploration</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-emerald-400" : "text-emerald-500"}`} />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}><strong>Guided Labs:</strong> Choose up to 2 specific labs to unlock permanently</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-emerald-400" : "text-emerald-500"}`} />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}><strong>Holo AI:</strong> 3 questions per day</span>
                </li>
                <li className="flex items-start gap-3 opacity-60">
                  <X className="w-5 h-5 shrink-0 mt-0.5 text-red-500" />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-400" : "text-slate-500"}`}>No Teacher Account, Analytics, or Management</span>
                </li>
              </ul>
            </div>
            <Link to="/sign-up" className={`w-full py-4 px-6 rounded-2xl font-bold flex items-center justify-center transition-all ${isDark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-900"}`}>
              Get Started Free
            </Link>
          </div>

          {/* Plus Tier (Center, Highlighted) */}
          <div className={`relative p-8 md:p-10 rounded-[2.5rem] border backdrop-blur-md transition-all duration-300 hover:-translate-y-3 flex flex-col justify-between transform md:scale-105 z-10
            ${isDark ? "bg-indigo-950/40 border-indigo-500/50 shadow-[0_0_50px_rgba(99,102,241,0.15)]" : "bg-indigo-600 border-indigo-500 shadow-2xl shadow-indigo-600/30"}`}>
            {/* Top Glow & Badge */}
            <div className="absolute top-0 inset-x-0 flex justify-center -mt-4">
              <span className={`px-4 py-1.5 text-xs font-bold uppercase tracking-widest rounded-full shadow-lg ${isDark ? "bg-indigo-500 text-white" : "bg-white text-indigo-700 font-black"}`}>Most Popular</span>
            </div>
            
            <div>
              <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 tracking-widest uppercase ${isDark ? "bg-indigo-500/20 text-indigo-300" : "bg-indigo-500/30 text-indigo-100"}`}>Single Classroom</div>
              <h3 className="text-3xl font-black mb-2 text-white">Plus Tier</h3>
              <p className={`text-sm mb-6 h-12 leading-relaxed ${isDark ? "text-indigo-200" : "text-indigo-100"}`}>Everything a single educator needs to run interactive chemistry labs for their students.</p>
              <div className="flex items-baseline gap-1 mb-1">
                <div className="text-5xl font-black text-white">RM 2,000</div>
                <div className={`text-lg font-bold ${isDark ? "text-indigo-300" : "text-indigo-200"}`}>/ yr</div>
              </div>
              <div className={`text-sm font-semibold mb-8 uppercase tracking-widest ${isDark ? "text-indigo-300" : "text-indigo-200"}`}>1 Teacher (Up to 100 students)</div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-emerald-400" />
                  <span className="text-sm font-medium text-white"><strong>Sandbox:</strong> 5 hours/day for student experiments</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-emerald-400" />
                  <span className="text-sm font-medium text-white"><strong>Guided Labs:</strong> Unlimited time & access to ALL standard science simulations (15+)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-emerald-400" />
                  <span className="text-sm font-medium text-white"><strong>Holo AI:</strong> 10 AI questions per student/day</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-emerald-400" />
                  <span className="text-sm font-medium text-white"><strong>Teacher Features:</strong> Dashboard, Assignments, Student progress</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-emerald-400" />
                  <span className="text-sm font-medium text-white">Standard Email Support</span>
                </li>
              </ul>
            </div>
            <Link to="/sign-up" className={`w-full py-4 px-6 rounded-2xl font-black flex items-center justify-center transition-all ${isDark ? "bg-indigo-500 hover:bg-indigo-400 text-white shadow-lg shadow-indigo-500/25" : "bg-white text-indigo-700 hover:bg-indigo-50 shadow-lg"}`}>
              Subscribe to Plus
            </Link>
          </div>

          {/* Custom Tier */}
          <div className={`relative p-8 md:p-10 rounded-[2.5rem] border backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 flex flex-col justify-between
            ${isDark ? "bg-gradient-to-b from-white/[0.02] to-fuchsia-500/[0.02] border-white/5 hover:border-fuchsia-500/30" : "bg-white border-slate-200 shadow-lg shadow-slate-200/40 hover:shadow-xl hover:border-fuchsia-300"}`}>
            <div>
              <div className="inline-block px-3 py-1 bg-fuchsia-100 dark:bg-fuchsia-500/10 rounded-full text-xs font-bold text-fuchsia-600 dark:text-fuchsia-400 mb-4 tracking-widest uppercase">School / District</div>
              <h3 className={`text-3xl font-black mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>Custom Tier</h3>
              <p className={`text-sm mb-6 h-12 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>Tailored, unlimited access for full science departments, whole schools, or districts.</p>
              <div className={`text-4xl md:text-5xl font-black mb-1 leading-tight ${isDark ? "text-white" : "text-slate-900"}`}>Contact Us</div>
              <div className={`text-sm font-semibold mb-8 uppercase tracking-widest mt-1 ${isDark ? "text-slate-500" : "text-slate-400"}`}>200+ students</div>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-fuchsia-400" : "text-fuchsia-500"}`} />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}><strong>Sandbox:</strong> Unlimited access</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-fuchsia-400" : "text-fuchsia-500"}`} />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}><strong>Guided Labs:</strong> Unlimited + custom curriculum alignment</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-fuchsia-400" : "text-fuchsia-500"}`} />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}><strong>Holo AI:</strong> Custom limits based on requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-fuchsia-400" : "text-fuchsia-500"}`} />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}><strong>Advanced Analytics:</strong> School-wide tracking & unlimited teacher accounts</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-fuchsia-400" : "text-fuchsia-500"}`} />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>Priority Support, SLA, & Dedicated Onboarding</span>
                </li>
              </ul>
            </div>
            <Link to="/contact" className={`w-full py-4 px-6 rounded-2xl font-bold flex items-center justify-center transition-all ${isDark ? "bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-400 border border-fuchsia-500/20" : "bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200"}`}>
              Contact Sales
            </Link>
          </div>

        </div>
      </div>
    </section>
  );
};

// ─── CTA SECTION ───────────────────────────────────────────────────────────

const CTASection: React.FC = () => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "dark";

  return (
    <section className={`py-24 px-6 transition-colors duration-500 ${isDark ? "bg-[#030712]" : "bg-[#f0f4ff]"}`}>
      <div className="max-w-3xl mx-auto text-center relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[200px] blur-[80px] pointer-events-none"
          style={{ background: isDark ? "rgba(97,218,251,0.06)" : "rgba(37,99,235,0.08)" }} />

        <div className={`relative z-10 p-10 rounded-3xl border backdrop-blur-sm ${isDark ? "border-white/8 bg-white/3" : "border-blue-100 bg-white shadow-xl shadow-blue-100/50"}`}>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-mono tracking-widest mb-6 ${isDark ? "border-[#61DAFB]/20 bg-[#61DAFB]/5 text-[#61DAFB]" : "border-blue-200 bg-blue-50 text-blue-600"}`}>
            START TODAY
          </div>
          <h2 className={`text-4xl md:text-5xl font-bold mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>Ready to enter the lab?</h2>
          <p className={`mb-10 max-w-md mx-auto ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            Join educators and students already using HoloLab. No installation required — works in any modern browser.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/sign-up"
              className={`group relative px-10 py-4 font-bold rounded-xl overflow-hidden transition-all hover:scale-[1.02] flex items-center justify-center gap-2 ${
                isDark
                  ? "bg-[#61DAFB] text-[#020617] hover:shadow-[0_0_40px_rgba(97,218,251,0.4)]"
                  : "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/25"
              }`}>
              <div className="absolute inset-0 bg-white/40 -translate-x-full group-hover:translate-x-full transition-transform duration-500 skew-x-12" />
              <span className="relative flex items-center gap-2">Create Free Account <ArrowRight size={18} /></span>
            </Link>
            <Link to="/sign-in"
              className={`px-10 py-4 rounded-xl border font-medium transition-all flex items-center justify-center gap-2 ${isDark ? "border-white/10 text-white hover:bg-white/5" : "border-slate-300 text-slate-700 hover:bg-white hover:shadow-md"}`}>
              I already have an account
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── FOOTER ────────────────────────────────────────────────────────────────

const FooterLink = ({ to, label }: { to: string; label: string }) => (
  <li>
    <Link to={to} className="flex items-center gap-2 hover:text-white transition-all duration-300 group">
      <span className="w-1.5 h-1.5 rounded-full bg-white/10 group-hover:bg-[#61DAFB] transition-colors" />
      <span className="group-hover:translate-x-1 transition-transform">{label}</span>
    </Link>
  </li>
);

const SocialBtn = ({ href, icon }: { href: string; icon: React.ReactNode }) => (
  <a href={href} target="_blank" rel="noreferrer"
    className="w-10 h-10 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center hover:bg-white/10 hover:border-white/20 hover:text-white transition-all hover:scale-105">
    {icon}
  </a>
);

const Footer: React.FC = () => (
  <footer className="relative bg-[#020617] text-slate-400 overflow-hidden pt-20 pb-10 border-t border-white/5">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-[#61DAFB] opacity-[0.03] blur-[100px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

        <div className="space-y-4">
          <Link to="/" className="flex items-center gap-3 w-fit">
            <img src="/logo_only.png" alt="HoloLab" className="w-10 h-10 object-contain" />
            <div>
              <span className="block text-base font-bold text-white">HoloLab</span>
              <span className="text-[11px] text-[#61DAFB]">Think Chemistry. Clearly.</span>
            </div>
          </Link>
          <p className="text-sm leading-relaxed max-w-xs">The world's first browser-based gesture laboratory. Democratizing science education through immersive technology.</p>
          <div className="text-xs text-gray-300">Made with 🧪 and ❤️ in KL</div>
        </div>

        <div>
          <h3 className="text-white font-bold mb-6 flex items-center gap-2"><Zap size={16} className="text-[#61DAFB]" /> Platform</h3>
          <ul className="space-y-3 text-sm">
            <FooterLink to="/" label="Home" />
            <FooterLink to="/sign-in" label="Sign In" />
            <FooterLink to="/sign-up" label="Create Account" />
            <FooterLink to="/select-role" label="Role Selection" />
          </ul>
        </div>

        <div>
          <h3 className="text-white font-bold mb-6 flex items-center gap-2"><Globe size={16} className="text-purple-400" /> Research</h3>
          <ul className="space-y-4 text-sm">
            <li>
              <div className="text-xs font-mono text-slate-500 mb-1 uppercase tracking-wider">Incubated At</div>
              <div className="flex items-center gap-2 text-gray-200"><span className="w-1 h-4 bg-purple-500 rounded-full" /> Universiti Kuala Lumpur</div>
            </li>
            <li>
              <div className="text-xs font-mono text-slate-500 mb-1 uppercase tracking-wider">Powered By</div>
              <div className="flex items-center gap-2 text-gray-200"><span className="w-1 h-4 bg-[#61DAFB] rounded-full" /> React 19 &amp; Gemini Pro</div>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-white font-bold mb-6 flex items-center gap-2"><ShieldCheck size={16} className="text-slate-200" /> Legal &amp; Social</h3>
          <ul className="space-y-3 text-sm mb-6">
            <FooterLink to="/privacy" label="Privacy Policy" />
            <FooterLink to="/terms" label="Terms of Service" />
          </ul>
          <div className="flex gap-3">
            <SocialBtn href="https://github.com" icon={<Github size={18} />} />
            <SocialBtn href="https://twitter.com" icon={<Twitter size={18} />} />
          </div>
        </div>
      </div>

      <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
        <div>© 2026 HoloLab Enterprise. <span className="hidden md:inline">All rights reserved.</span></div>
        <div className="flex items-center gap-4 font-mono">
          <span className="flex items-center gap-1.5">Made with <Heart size={10} className="text-red-500 fill-current mx-0.5" /> in KL</span>
          <span className="w-px h-3 bg-white/10" />
          <span>v2.4.0 (Alpha)</span>
        </div>
      </div>
    </div>
  </footer>
);

// ─── ROOT ──────────────────────────────────────────────────────────────────

const LandingPage: React.FC = () => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === "dark";

  return (
    <div className={`relative w-full min-h-screen transition-colors duration-500 overflow-x-hidden ${isDark ? "bg-[#030712]" : "bg-[#f0f4ff]"}`}>
      <Header />
      <HeroSection />
      <FeaturesSection />
      <PricingSection />
      <AboutUsSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default LandingPage;
